import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { DownloadSimple, FileDoc, FileXls, SpinnerGap, WarningCircle } from '@phosphor-icons/react';
import { sanitizeRichHtml } from '@/utils/safeHtml';
import { isLegacyWordDoc } from '@/utils/officeDocuments';
import './OfficeDocumentViewer.styles.css';

const SAFE_HREF_RE = /^(https?:|mailto:|tel:|#)/i;

const fetchArrayBuffer = async (url) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.arrayBuffer();
};

const readSpreadsheet = async (url) => {
  const [XLSX, data] = await Promise.all([import('xlsx'), fetchArrayBuffer(url)]);
  const workbook = XLSX.read(data, { type: 'array', cellDates: true });
  return workbook.SheetNames.map((name) => ({
    name,
    html: sanitizeRichHtml(XLSX.utils.sheet_to_html(workbook.Sheets[name], { header: '', footer: '' })),
  }));
};

// Renders into a detached element so a cancelled load never writes into the page.
const renderWordDocument = async (url) => {
  const [{ renderAsync }, data] = await Promise.all([import('docx-preview'), fetchArrayBuffer(url)]);
  const container = document.createElement('div');
  await renderAsync(data, container, container, {
    className: 'docx',
    inWrapper: true,
    breakPages: true,
    ignoreLastRenderedPageBreak: true,
    useBase64URL: true,
  });
  // Links come from the customer's file: keep only plain web / mail / phone ones.
  container.querySelectorAll('a[href]').forEach((anchor) => {
    const href = anchor.getAttribute('href') || '';
    if (!SAFE_HREF_RE.test(href.trim())) {
      anchor.removeAttribute('href');
      return;
    }
    if (/^https?:/i.test(href)) {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
    }
  });
  return container;
};

/**
 * Renders an Excel (.xlsx/.xls/.csv) or Word (.docx) file in the browser.
 * Legacy .doc and files that fail to parse get a download card.
 */
const OfficeDocumentViewer = ({ url, kind, title }) => {
  const [status, setStatus] = useState('loading');
  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const wordContainerRef = useRef(null);
  const unsupported = kind === 'word' && isLegacyWordDoc(url);

  useEffect(() => {
    if (unsupported) {
      setStatus('unsupported');
      return undefined;
    }
    let cancelled = false;
    setStatus('loading');
    setSheets([]);
    setActiveSheet(0);

    const load = async () => {
      try {
        if (kind === 'spreadsheet') {
          const parsedSheets = await readSpreadsheet(url);
          if (!cancelled) setSheets(parsedSheets);
        } else {
          const rendered = await renderWordDocument(url);
          if (!cancelled && wordContainerRef.current) {
            wordContainerRef.current.replaceChildren(rendered);
          }
        }
        if (!cancelled) setStatus('ready');
      } catch (error) {
        console.error('Failed to render document', url, error);
        if (!cancelled) setStatus('error');
      }
    };
    load();
    return () => { cancelled = true; };
  }, [url, kind, unsupported]);

  const Icon = kind === 'spreadsheet' ? FileXls : FileDoc;
  const iconClass = kind === 'spreadsheet' ? 'text-green-700' : 'text-blue-700';
  const showFallback = status === 'error' || status === 'unsupported';

  return (
    <div
      className="office-doc-viewer"
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div className="office-doc-toolbar">
        <Icon size={20} weight="duotone" className={iconClass} />
        <span className="office-doc-title" title={title}>{title}</span>
        <a className="office-doc-download" href={url} download target="_blank" rel="noopener noreferrer">
          <DownloadSimple size={16} weight="bold" />
          Descarcă
        </a>
      </div>

      {kind === 'spreadsheet' && sheets.length > 1 && (
        <div className="office-doc-sheet-tabs">
          {sheets.map((sheet, index) => (
            <button
              key={sheet.name}
              type="button"
              className={index === activeSheet ? 'active' : ''}
              onClick={() => setActiveSheet(index)}
            >
              {sheet.name}
            </button>
          ))}
        </div>
      )}

      <div className="office-doc-body">
        {status === 'loading' && (
          <div className="office-doc-state">
            <SpinnerGap size={32} className="animate-spin" />
            <span>Se încarcă documentul…</span>
          </div>
        )}
        {showFallback && (
          <div className="office-doc-state">
            {status === 'unsupported'
              ? <Icon size={48} weight="duotone" className={iconClass} />
              : <WarningCircle size={40} weight="duotone" />}
            <span>
              {status === 'unsupported'
                ? 'Formatul .doc nu se poate previzualiza. Descarcă fișierul ca să-l deschizi.'
                : 'Documentul nu a putut fi afișat. Descarcă fișierul ca să-l deschizi.'}
            </span>
          </div>
        )}
        {kind === 'spreadsheet' && status === 'ready' && sheets[activeSheet] && (
          <div
            className="office-doc-sheet"
            // Sanitized above: tables only, no attributes besides colspan/rowspan/class.
            dangerouslySetInnerHTML={{ __html: sheets[activeSheet].html }}
          />
        )}
        {kind === 'word' && !unsupported && (
          <div
            ref={wordContainerRef}
            className="office-doc-word"
            style={{ display: status === 'ready' ? 'block' : 'none' }}
          />
        )}
      </div>
    </div>
  );
};

OfficeDocumentViewer.propTypes = {
  url: PropTypes.string.isRequired,
  kind: PropTypes.oneOf(['spreadsheet', 'word']).isRequired,
  title: PropTypes.string,
};

export default OfficeDocumentViewer;
